/**
 * Service de gestion des mises à jour de l'application
 * Ce service vérifie périodiquement si une nouvelle version de l'application est disponible
 * et l'installe automatiquement sans nécessiter l'intervention de l'utilisateur
 */

// Version actuelle de l'application (à mettre à jour à chaque déploiement)
const APP_VERSION = '1.1.1'; 
const VERSION_CHECK_INTERVAL = 15 * 60 * 1000; // 15 minutes
const FORCE_UPDATE_INTERVAL = 24 * 60 * 60 * 1000; // 24 heures
const AUTO_UPDATE_DELAY = 10 * 1000; // 10 secondes d'attente avant d'appliquer une mise à jour automatique

class UpdateService {
  constructor() {
    this.lastUpdateCheck = null;
    this.updateAvailable = false;
    this.installPrompted = false;
    this.registeringNewWorker = null;
    this.toast = null;
    this.autoUpdateEnabled = true; // Activer les mises à jour automatiques par défaut
    this.updateCheckTimer = null;
    this.lastForceUpdate = parseInt(localStorage.getItem('lastForceUpdate') || '0');
  }

  /**
   * Initialiser le service de mise à jour
   * @param {Object} toast - Service Toast de PrimeVue
   */
  init(toast) {
    this.toast = toast;
    
    // Vérifier au démarrage s'il y a des mises à jour
    this.checkForUpdates(true);
    
    // Configurer la vérification périodique des mises à jour
    this.startAutoUpdateChecker();

    // Vérifier s'il faut forcer une mise à jour
    this.checkForceUpdate();

    // Configurer les écouteurs d'événements
    window.addEventListener('online', () => {
      console.log('Connexion internet rétablie, vérification des mises à jour...');
      this.checkForUpdates(true);
      
      // Redémarrer le vérificateur de mises à jour automatiques
      this.startAutoUpdateChecker();
    });
    
    window.addEventListener('offline', () => {
      // Arrêter le vérificateur de mises à jour automatiques lorsqu'on est hors ligne
      this.stopAutoUpdateChecker();
    });
  }
  
  /**
   * Démarrer la vérification automatique des mises à jour
   */
  startAutoUpdateChecker() {
    // Arrêter l'ancien timer s'il existe
    this.stopAutoUpdateChecker();
    
    // Démarrer un nouveau timer seulement si l'application est en ligne
    if (navigator.onLine && this.autoUpdateEnabled) {
      this.updateCheckTimer = setInterval(() => {
        this.checkForUpdates(true);
      }, VERSION_CHECK_INTERVAL);
      
      console.log(`Vérificateur de mises à jour automatiques démarré (intervalle: ${VERSION_CHECK_INTERVAL / 60000} minutes)`);
    }
  }
  
  /**
   * Arrêter la vérification automatique des mises à jour
   */
  stopAutoUpdateChecker() {
    if (this.updateCheckTimer) {
      clearInterval(this.updateCheckTimer);
      this.updateCheckTimer = null;
      console.log('Vérificateur de mises à jour automatiques arrêté');
    }
  }

  /**
   * Vérifie si le service worker est disponible et enregistré
   */
  isServiceWorkerAvailable() {
    return 'serviceWorker' in navigator && navigator.serviceWorker.controller;
  }

  /**
   * Vérifie si une mise à jour de l'application est disponible
   * @param {boolean} autoApply - Si true, applique automatiquement la mise à jour si disponible
   */
  async checkForUpdates(autoApply = false) {
    if (!this.isServiceWorkerAvailable() || !navigator.onLine) {
      return false;
    }

    this.lastUpdateCheck = Date.now();
    
    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.update();
      
      // Si une mise à jour est disponible et que l'application est configurée pour appliquer automatiquement,
      // on va l'installer sans demander à l'utilisateur
      if (this.updateAvailable && autoApply && this.autoUpdateEnabled) {
        console.log('Mise à jour disponible détectée, application automatique dans 10 secondes');
        
        // Attendre un court délai avant d'appliquer la mise à jour pour laisser l'application se stabiliser
        setTimeout(() => {
          this.applyUpdate(true);
        }, AUTO_UPDATE_DELAY);
      }
      
      return true;
    } catch (error) {
      console.error('Erreur lors de la vérification des mises à jour:', error);
      return false;
    }
  }

  /**
   * Vérifie s'il faut forcer une mise à jour basée sur le temps écoulé
   */
  checkForceUpdate() {
    const now = Date.now();
    
    // Si la dernière mise à jour forcée date de plus de 24h
    if (now - this.lastForceUpdate > FORCE_UPDATE_INTERVAL) {
      // Forcer une mise à jour après un délai aléatoire (pour éviter que tous les clients ne rechargent en même temps)
      const randomDelay = Math.floor(Math.random() * 30 * 60 * 1000); // 0-30 minutes
      
      setTimeout(() => {
        console.log('Mise à jour forcée par intervalle de temps');
        this.applyUpdate(true);
      }, randomDelay);
    }
  }

  /**
   * Configure les écouteurs pour détecter les mises à jour du service worker
   * @param {Object} registration - L'objet d'enregistrement du service worker
   */
  setupUpdateListeners(registration) {
    if (!registration) return;
    
    registration.onupdatefound = () => {
      this.registeringNewWorker = registration.installing;
      
      if (this.registeringNewWorker) {
        this.registeringNewWorker.onstatechange = () => {
          if (this.registeringNewWorker.state === 'installed' && navigator.serviceWorker.controller) {
            // Nouvelle version disponible
            this.updateAvailable = true;
            
            // Si les mises à jour automatiques sont activées, appliquer sans demander
            if (this.autoUpdateEnabled) {
              console.log('Nouvelle version détectée, application automatique dans 10 secondes');
              setTimeout(() => {
                this.applyUpdate(true);
              }, AUTO_UPDATE_DELAY);
            } else if (!this.installPrompted) {
              // Sinon, demander à l'utilisateur
              this.promptForUpdate();
            }
          }
        };
      }
    };
  }

  /**
   * Affiche une notification pour proposer la mise à jour
   * (Cette méthode n'est utilisée que si les mises à jour automatiques sont désactivées)
   */
  promptForUpdate() {
    if (!this.toast || this.installPrompted) return;
    
    this.installPrompted = true;
    
    this.toast.add({
      severity: 'info',
      summary: 'Mise à jour disponible',
      detail: 'Une nouvelle version de l\'application est disponible. Voulez-vous l\'installer maintenant?',
      life: 20000,
      closable: true,
      action: {
        label: 'Mettre à jour',
        icon: 'pi pi-refresh',
        onClick: () => this.applyUpdate()
      }
    });
    
    // Prévoir une mise à jour automatique après un certain délai, même si l'utilisateur n'a pas répondu
    setTimeout(() => {
      if (this.updateAvailable) {
        console.log('Application de la mise à jour automatique après délai');
        this.applyUpdate();
      }
    }, 10 * 60 * 1000); // 10 minutes
  }

  /**
   * Applique la mise à jour en rechargeant la page
   * @param {boolean} forced - Indique si la mise à jour est forcée
   */
  applyUpdate(forced = false) {
    // Mémoriser le timestamp de la dernière mise à jour forcée
    if (forced) {
      localStorage.setItem('lastForceUpdate', Date.now().toString());
    }
    
    // Afficher un message temporaire de chargement
    if (this.toast) {
      this.toast.add({
        severity: 'info',
        summary: 'Mise à jour en cours',
        detail: 'Application en cours de mise à jour...',
        life: 3000
      });
    }
    
    // Déclencher le skipWaiting sur le service worker
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
    }
    
    // Laisser un peu de temps pour que le message apparaisse
    setTimeout(() => {
      // Recharger la page
      window.location.reload();
    }, 1000);
  }
  
  /**
   * Active ou désactive les mises à jour automatiques
   * @param {boolean} enabled - Si true, active les mises à jour automatiques
   */
  setAutoUpdateEnabled(enabled) {
    this.autoUpdateEnabled = enabled;
    
    if (enabled) {
      this.startAutoUpdateChecker();
      
      // Si une mise à jour est déjà disponible, l'appliquer maintenant
      if (this.updateAvailable) {
        setTimeout(() => {
          this.applyUpdate(true);
        }, AUTO_UPDATE_DELAY);
      }
    } else {
      this.stopAutoUpdateChecker();
    }
    
    return this.autoUpdateEnabled;
  }
}

export default new UpdateService(); 